import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { apiRequest, ApiError } from "../../api/http";
import {
  CompanyProfileBrand,
  CompanyProfileCategory,
  MinimumWageSetting,
  MinimumWageUpsertPayload,
} from "../../types/admin";

type CompanyProfileListResponse = {
  items: Array<
    CompanyProfileCategory & {
      brands: CompanyProfileBrand[];
    }
  >;
};

type AppBasicSettingsState = {
  currentWage: MinimumWageSetting | null;
  wageLoading: boolean;
  wageError: string | null;
  wageSaving: boolean;
  wageSaveError: string | null;
  categories: CompanyProfileCategory[];
  brandsByCategory: Record<number, CompanyProfileBrand[]>;
  categoryLoading: boolean;
  categoryError: string | null;
  categorySaving: boolean;
  brandSaving: boolean;
  selectedCategoryId: number | null;
};

const initialState: AppBasicSettingsState = {
  currentWage: null,
  wageLoading: false,
  wageError: null,
  wageSaving: false,
  wageSaveError: null,
  categories: [],
  brandsByCategory: {},
  categoryLoading: false,
  categoryError: null,
  categorySaving: false,
  brandSaving: false,
  selectedCategoryId: null,
};

export const fetchMinimumWage = createAsyncThunk<
  MinimumWageSetting | null,
  number | void,
  { rejectValue: string }
>("appBasicSettings/fetchMinimumWage", async (year, { rejectWithValue }) => {
  try {
    const query = year ? `?year=${year}` : "";
    return await apiRequest<MinimumWageSetting>(`/admin/settings/minimum-wage${query}`);
  } catch (error) {
    // 미등록 연도는 빈 입력으로 취급 (Figma처럼 에러 문구 없이 표시)
    if (error instanceof ApiError && error.status === 404) {
      return null;
    }
    const message = error instanceof Error ? error.message : "최저시급 조회에 실패했습니다.";
    return rejectWithValue(message);
  }
});

export const upsertMinimumWage = createAsyncThunk<
  MinimumWageSetting,
  MinimumWageUpsertPayload,
  { rejectValue: string }
>("appBasicSettings/upsertMinimumWage", async (payload, { rejectWithValue }) => {
  try {
    return await apiRequest<MinimumWageSetting>("/admin/settings/minimum-wage", {
      method: "PUT",
      body: payload,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : "최저시급 저장에 실패했습니다.";
    return rejectWithValue(message);
  }
});

export const fetchCompanyProfiles = createAsyncThunk<
  CompanyProfileListResponse,
  void,
  { rejectValue: string }
>("appBasicSettings/fetchCompanyProfiles", async (_, { rejectWithValue }) => {
  try {
    return await apiRequest<CompanyProfileListResponse>("/admin/company-profile");
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return { items: [] };
    }
    const message = error instanceof Error ? error.message : "업체 프로필 목록 조회에 실패했습니다.";
    return rejectWithValue(message);
  }
});

export const createCompanyProfileCategory = createAsyncThunk<
  CompanyProfileCategory,
  { name: string; sort_order?: number },
  { rejectValue: string }
>("appBasicSettings/createCategory", async (payload, { rejectWithValue }) => {
  try {
    return await apiRequest<CompanyProfileCategory>("/admin/company-profile", {
      method: "POST",
      body: { type: "category", ...payload },
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : "분류 추가에 실패했습니다.";
    return rejectWithValue(message);
  }
});

export const updateCompanyProfileCategory = createAsyncThunk<
  CompanyProfileCategory,
  { categoryId: number; name: string; sort_order?: number },
  { rejectValue: string }
>("appBasicSettings/updateCategory", async ({ categoryId, ...payload }, { rejectWithValue }) => {
  try {
    return await apiRequest<CompanyProfileCategory>(`/admin/company-profile/${categoryId}`, {
      method: "PATCH",
      body: { type: "category", ...payload },
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : "분류 수정에 실패했습니다.";
    return rejectWithValue(message);
  }
});

export const createCompanyProfileBrand = createAsyncThunk<
  CompanyProfileBrand,
  { categoryId: number; formData: FormData },
  { rejectValue: string }
>("appBasicSettings/createBrand", async ({ categoryId, formData }, { rejectWithValue }) => {
  try {
    formData.set("type", "brand");
    formData.set("category_id", String(categoryId));
    return await apiRequest<CompanyProfileBrand>("/admin/company-profile", {
      method: "POST",
      body: formData,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : "브랜드 로고 추가에 실패했습니다.";
    return rejectWithValue(message);
  }
});

export const updateCompanyProfileBrand = createAsyncThunk<
  CompanyProfileBrand,
  { brandId: number; formData: FormData },
  { rejectValue: string }
>("appBasicSettings/updateBrand", async ({ brandId, formData }, { rejectWithValue }) => {
  try {
    formData.set("type", "brand");
    return await apiRequest<CompanyProfileBrand>(`/admin/company-profile/${brandId}`, {
      method: "PATCH",
      body: formData,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : "브랜드 로고 수정에 실패했습니다.";
    return rejectWithValue(message);
  }
});

const appBasicSettingsSlice = createSlice({
  name: "appBasicSettings",
  initialState,
  reducers: {
    setSelectedCategoryId(state, action: PayloadAction<number | null>) {
      state.selectedCategoryId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMinimumWage.pending, (state) => {
        state.wageLoading = true;
        state.wageError = null;
      })
      .addCase(fetchMinimumWage.fulfilled, (state, action) => {
        state.wageLoading = false;
        state.currentWage = action.payload;
        state.wageError = null;
      })
      .addCase(fetchMinimumWage.rejected, (state, action) => {
        state.wageLoading = false;
        state.currentWage = null;
        state.wageError = action.payload ?? "최저시급 조회에 실패했습니다.";
      })
      .addCase(upsertMinimumWage.pending, (state) => {
        state.wageSaving = true;
        state.wageSaveError = null;
      })
      .addCase(upsertMinimumWage.fulfilled, (state, action) => {
        state.wageSaving = false;
        state.currentWage = action.payload;
      })
      .addCase(upsertMinimumWage.rejected, (state, action) => {
        state.wageSaving = false;
        state.wageSaveError = action.payload ?? "최저시급 저장에 실패했습니다.";
      })
      .addCase(fetchCompanyProfiles.pending, (state) => {
        state.categoryLoading = true;
        state.categoryError = null;
      })
      .addCase(fetchCompanyProfiles.fulfilled, (state, action) => {
        state.categoryLoading = false;
        state.categories = action.payload.items.map((item) => ({
          category_id: item.category_id,
          name: item.name,
          sort_order: item.sort_order,
          brand_count: item.brands?.length ?? item.brand_count ?? 0,
          created_at: item.created_at ?? null,
          updated_at: item.updated_at ?? null,
        }));
        state.brandsByCategory = {};
        action.payload.items.forEach((item) => {
          state.brandsByCategory[item.category_id] = (item.brands || []).map((brand) => ({
            ...brand,
            category_id: item.category_id,
          }));
        });
        if (state.selectedCategoryId == null && state.categories.length > 0) {
          state.selectedCategoryId = state.categories[0].category_id;
        }
      })
      .addCase(fetchCompanyProfiles.rejected, (state, action) => {
        state.categoryLoading = false;
        state.categoryError = action.payload ?? "업체 프로필 목록 조회에 실패했습니다.";
      })
      .addCase(createCompanyProfileCategory.pending, (state) => {
        state.categorySaving = true;
      })
      .addCase(createCompanyProfileCategory.fulfilled, (state, action) => {
        state.categorySaving = false;
        state.categories = [
          ...state.categories,
          { ...action.payload, brand_count: action.payload.brand_count ?? 0 },
        ];
        state.brandsByCategory[action.payload.category_id] = [];
        state.selectedCategoryId = action.payload.category_id;
      })
      .addCase(createCompanyProfileCategory.rejected, (state) => {
        state.categorySaving = false;
      })
      .addCase(updateCompanyProfileCategory.fulfilled, (state, action) => {
        state.categories = state.categories.map((item) =>
          item.category_id === action.payload.category_id
            ? { ...item, ...action.payload, brand_count: item.brand_count }
            : item
        );
      })
      .addCase(createCompanyProfileBrand.pending, (state) => {
        state.brandSaving = true;
      })
      .addCase(createCompanyProfileBrand.fulfilled, (state, action) => {
        state.brandSaving = false;
        const categoryId = action.payload.category_id;
        const prev = state.brandsByCategory[categoryId] || [];
        state.brandsByCategory[categoryId] = [...prev, action.payload].sort(
          (a, b) => a.sort_order - b.sort_order
        );
        const category = state.categories.find((item) => item.category_id === categoryId);
        if (category) {
          category.brand_count = state.brandsByCategory[categoryId].length;
        }
      })
      .addCase(createCompanyProfileBrand.rejected, (state) => {
        state.brandSaving = false;
      })
      .addCase(updateCompanyProfileBrand.pending, (state) => {
        state.brandSaving = true;
      })
      .addCase(updateCompanyProfileBrand.fulfilled, (state, action) => {
        state.brandSaving = false;
        const categoryId = action.payload.category_id;
        const prev = state.brandsByCategory[categoryId] || [];
        state.brandsByCategory[categoryId] = prev
          .map((item) => (item.brand_id === action.payload.brand_id ? action.payload : item))
          .sort((a, b) => a.sort_order - b.sort_order);
      })
      .addCase(updateCompanyProfileBrand.rejected, (state) => {
        state.brandSaving = false;
      });
  },
});

export const { setSelectedCategoryId } = appBasicSettingsSlice.actions;
export default appBasicSettingsSlice.reducer;
